import { GeneratedPlansMap } from '@lp/features/RoutePlanner/types';
import { RoutePlanMetrics } from '@/api/apis/mission-planner';

export const routePlanAirTimeDiff = (
  benchmarkMetrics: RoutePlanMetrics,
  routeMetrics: RoutePlanMetrics,
): { percents: number; timeMs: number; isNegative: boolean } => {
  const bFlightTimeMs = benchmarkMetrics.totalFlightDuration * 1000;
  const rFlightTimeMs = routeMetrics.totalFlightDuration * 1000;
  const timeDiff = bFlightTimeMs - rFlightTimeMs;
  const timeDiffMs = Math.abs(timeDiff);
  const isNegative = timeDiff > 0;
  const percentsDiff = Math.round((timeDiffMs / bFlightTimeMs) * 100);

  return {
    percents: Number.isFinite(percentsDiff) ? percentsDiff : 0,
    timeMs: timeDiffMs,
    isNegative,
  };
};

/**
 * Surgeon trips and commercial flights arrive grouped by the position of their main plan group, while
 * the generated plans are stored by plan id. Empty groups and groups without a matching main plan
 * (a group that produced no routes is never stored) are dropped.
 */
export const mapGroupsToPlanIds = <T>(
  basePlans: GeneratedPlansMap,
  groups: T[][] | Record<number, T[]>,
): Record<string, T[]> => {
  const planIdByIndex = new Map<number, string>();

  for (const plan of Object.values(basePlans)) {
    if (!planIdByIndex.has(plan.index)) planIdByIndex.set(plan.index, plan.id);
  }

  return Object.entries(groups).reduce<Record<string, T[]>>((acc, [index, group]) => {
    const planId = planIdByIndex.get(Number(index));

    if (!planId || !group.length) return acc;

    return { ...acc, [planId]: group };
  }, {});
};

/**
 * Streaming pollers redeliver every group on each tick, mostly unchanged. The tracker remembers how
 * many raw items each group last carried and reports only the groups that have since grown, so a
 * tick converts what is new instead of the whole result.
 */
export const createGroupGrowthTracker = () => {
  const seenLengths: number[] = [];

  return (groups: unknown[][]): number[] =>
    groups.reduce<number[]>((grown, group, index) => {
      if (group.length <= (seenLengths[index] ?? 0)) return grown;

      seenLengths[index] = group.length;

      return [...grown, index];
    }, []);
};

/** Converts only the listed groups, keyed by their index. Returns `null` when there is nothing to convert. */
export const convertGroups = async <S, T>(
  indexes: number[],
  groups: S[][],
  convert: (item: S, groupIndex: number, planIndex: number) => Promise<T>,
): Promise<Record<number, T[]> | null> => {
  if (!indexes.length) return null;

  const converted = await Promise.all(
    indexes.map(async (groupIndex) =>
      Promise.all(groups[groupIndex].map((item, planIndex) => convert(item, groupIndex, planIndex))),
    ),
  );

  return Object.fromEntries(indexes.map((groupIndex, position) => [groupIndex, converted[position]]));
};
